/**
 * The change lifecycle, as a chevron pipeline — Step 49.
 *
 * Open → Planning → Awaiting approval → Pending release → Pending review →
 * Closed. Cancelled is reachable from anywhere and so is not in the sequence;
 * it sits beside it.
 *
 * **The stepper greys out the same transitions the API refuses, using the
 * same function.** `changeGate` is pure and lives in `@itsm/shared`, so a
 * step that cannot be reached is dimmed *and* explains why in its tooltip,
 * rather than being clickable and then rejected. Two copies of that logic
 * would eventually disagree, and the one people would trust is the one that
 * is wrong — the UI.
 *
 * Clicking is still a real PATCH, so the API's refusal remains the actual
 * rule; this only stops people walking into it.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import {
  CHANGE_STAGES,
  changeGate,
  type ApprovalState,
  type ChangeKind,
  type ChangeStage,
} from '@itsm/shared';

const LABEL: Record<string, string> = {
  open: 'Open',
  planning: 'Planning',
  awaiting_approval: 'Awaiting approval',
  pending_release: 'Pending release',
  pending_review: 'Pending review',
  closed: 'Closed',
};

export function ChangeStepper({
  id,
  status,
  plan,
  approval,
  mandatoryBoard,
  canEdit,
}: {
  id: string;
  status: string | null;
  plan: {
    kind: string;
    justification: string | null;
    backoutPlan: string | null;
    testPlan: string | null;
    reviewOutcome?: string | null;
    reviewNote?: string | null;
  } | null;
  /** Where it stands on approval. `none` — nobody was ever asked — passes:
   *  a change with no CAB is not waiting on anything. See `changeGate`. */
  approval: ApprovalState;
  /** Step 195 — named in the refusal, so "needs approval" says who from. */
  mandatoryBoard?: string | null;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const current = (status ?? 'open') as ChangeStage;
  const cancelled = status === 'cancelled';
  const currentIndex = CHANGE_STAGES.indexOf(current);

  const move = useMutation({
    mutationFn: (to: string) => apiSend(`/api/tickets/${id}`, 'PATCH', { status: to }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not move this change.');
      void qc.invalidateQueries({ queryKey: qk.tickets.detail(id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.activity(id) });
    },
  });

  const refusalFor = (to: ChangeStage) =>
    changeGate(current, to, {
      kind: (plan?.kind ?? 'normal') as ChangeKind,
      justification: plan?.justification ?? null,
      backoutPlan: plan?.backoutPlan ?? null,
      testPlan: plan?.testPlan ?? null,
      approval,
      mandatoryBoard: mandatoryBoard ?? null,
      outcome: plan?.reviewOutcome ?? null,
      outcomeNote: plan?.reviewNote ?? null,
    });

  return (
    <div className="flex items-center gap-1 overflow-x-auto border-b px-4 py-2">
      {CHANGE_STAGES.map((stage, i) => {
        const isCurrent = !cancelled && stage === current;
        const isPast = !cancelled && i < currentIndex;
        const refusal = refusalFor(stage);
        const blocked = !!refusal && !isCurrent && !isPast;
        return (
          <button
            key={stage}
            type="button"
            disabled={!canEdit || isCurrent || cancelled || move.isPending}
            onClick={() => {
              // The refusal is shown rather than swallowed — a dead button
              // that says nothing is how people conclude the app is broken.
              if (refusal) return toast.error(refusal);
              move.mutate(stage);
            }}
            title={refusal ?? (isCurrent ? 'Where this change is now' : `Move to ${LABEL[stage]}`)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              isCurrent && 'bg-brand text-white',
              isPast && 'text-emerald-700 hover:bg-accent dark:text-emerald-400',
              !isCurrent &&
                !isPast &&
                !blocked &&
                'text-muted-foreground hover:bg-accent hover:text-foreground',
              // Dimmed, not hidden: knowing a step EXISTS and why you cannot
              // reach it is the useful half.
              blocked && 'text-muted-foreground/50',
              cancelled && 'opacity-40',
            )}
          >
            {isPast && <Check className="h-3 w-3" />}
            {LABEL[stage]}
          </button>
        );
      })}
      <span className="ml-auto flex items-center gap-2 pl-2">
        {cancelled ? (
          <span className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-muted-foreground">
            <X className="h-3.5 w-3.5" /> Cancelled
          </span>
        ) : (
          canEdit && (
            <button
              type="button"
              onClick={() => move.mutate('cancelled')}
              className="whitespace-nowrap rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-destructive"
            >
              Cancel change
            </button>
          )
        )}
      </span>
    </div>
  );
}
